import React from "react";
import { Chip, Box, Typography, Tooltip } from "@mui/material";
import {
  CheckCircle,
  Warning,
  Error,
  Schedule,
  Alarm,
} from "@mui/icons-material";

// Due Badge Component
export const DueBadge = ({
  dueBucket,
  dueDate = null,
  size = "small",
  showDaysRemaining = true,
}) => {
  const getDaysUntilDue = (dateString) => {
    if (!dateString) return null;
    const today = new Date();
    const due = new Date(dateString);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = dueDate ? getDaysUntilDue(dueDate) : null;

  const bucketConfig = {
    "75%": {
      color: "success",
      label: "75%",
      icon: <CheckCircle fontSize="small" />,
      description: "More than 3 days remaining",
      bgColor: "#e8f5e8",
      textColor: "#2e7d32",
    },
    "50%": {
      color: "info",
      label: "50%",
      icon: <Schedule fontSize="small" />,
      description: "2-3 days remaining",
      bgColor: "#e3f2fd",
      textColor: "#1565c0",
    },
    "25%": {
      color: "warning",
      label: "25%",
      icon: <Warning fontSize="small" />,
      description: "1 day remaining",
      bgColor: "#fff3e0",
      textColor: "#ef6c00",
    },
    past_due: {
      color: "error",
      label: "OVERDUE",
      icon: <Error fontSize="small" />,
      description: "Past due date",
      bgColor: "#ffebee",
      textColor: "#c62828",
    },
  };

  const config = bucketConfig[dueBucket] || {
    color: "default",
    label: dueBucket || "Unknown",
    icon: <Alarm fontSize="small" />,
    description: "Unknown due status",
    bgColor: "#f5f5f5",
    textColor: "#666666",
  };

  const formatDaysRemaining = () => {
    if (daysRemaining === null) return "";

    if (daysRemaining < 0) {
      return `${Math.abs(daysRemaining)} days overdue`;
    } else if (daysRemaining === 0) {
      return "Due today";
    } else {
      return `${daysRemaining} days left`;
    }
  };

  const chip = (
    <Chip
      icon={config.icon}
      label={config.label}
      color={config.color}
      size={size}
      variant="filled"
      sx={{
        fontWeight: "bold",
        ...(dueBucket === "past_due" && {
          animation: "pulse 2s infinite",
          "@keyframes pulse": {
            "0%": { opacity: 1 },
            "50%": { opacity: 0.7 },
            "100%": { opacity: 1 },
          },
        }),
      }}
    />
  );

  const content =
    showDaysRemaining && daysRemaining !== null ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {chip}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontStyle: daysRemaining < 0 ? "italic" : "normal",
            color: daysRemaining < 0 ? "error.main" : "text.secondary",
            fontWeight: daysRemaining <= 1 ? "bold" : "normal",
          }}
        >
          {formatDaysRemaining()}
        </Typography>
      </Box>
    ) : (
      chip
    );

  return (
    <Tooltip
      title={`${config.description}${dueDate ? ` (Due: ${new Date(dueDate).toLocaleDateString()})` : ""}`}
      arrow
    >
      <Box component="span">{content}</Box>
    </Tooltip>
  );
};

// Due Status Summary Component (for dashboard)
export const DueStatusSummary = ({ counts }) => {
  const buckets = [
    { key: "75%", label: "75%", color: "success" },
    { key: "50%", label: "50%", color: "info" },
    { key: "25%", label: "25%", color: "warning" },
    { key: "past_due", label: "Overdue", color: "error" },
  ];

  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
      {buckets.map(({ key, label, color }) => (
        <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Chip label={label} color={color} size="small" variant="filled" />
          <Typography variant="body2" fontWeight="bold">
            {counts[key] || 0}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// Due Bucket Filter Component
export const DueBucketFilter = ({
  selectedBucket,
  onBucketChange,
  counts = {},
}) => {
  const buckets = [
    { key: "", label: "All", color: "default" },
    { key: "75%", label: "75%", color: "success" },
    { key: "50%", label: "50%", color: "info" },
    { key: "25%", label: "25%", color: "warning" },
    { key: "past_due", label: "Overdue", color: "error" },
  ];

  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      {buckets.map(({ key, label, color }) => {
        const isSelected = selectedBucket === key;
        const count =
          key === ""
            ? Object.values(counts).reduce((a, b) => a + b, 0)
            : counts[key] || 0;

        return (
          <Chip
            key={key}
            label={`${label} (${count})`}
            color={color}
            variant={isSelected ? "filled" : "outlined"}
            clickable
            onClick={() => onBucketChange(key)}
            sx={{
              fontWeight: isSelected ? "bold" : "normal",
              ...(key === "past_due" &&
                count > 0 &&
                !isSelected && {
                  animation: "pulse 2s infinite",
                  "@keyframes pulse": {
                    "0%": { opacity: 1 },
                    "50%": { opacity: 0.7 },
                    "100%": { opacity: 1 },
                  },
                }),
            }}
          />
        );
      })}
    </Box>
  );
};

// Utility function to calculate due bucket from date
export const calculateDueBucket = (dueDate) => {
  if (!dueDate) return null;

  const today = new Date();
  const due = new Date(dueDate);
  const diffTime = due - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return "past_due";
  } else if (diffDays <= 1) {
    return "25%";
  } else if (diffDays <= 3) {
    return "50%";
  } else {
    return "75%";
  }
};

export default DueBadge;
